import React, { useCallback, useContext, useMemo } from 'react';
import { ApolloError, gql, useApolloClient } from '@apollo/client';
import { SnippetsController } from './Snippets.controller';
import type { DeleteSnippetMutationOptions } from 'models/graphql';
import { useDeleteSnippetMutation, useGetSnippetsLazyQuery } from 'models/graphql';
import { SnippetCardViewProps } from './SnippetCard/SnippetCard.view';
import { SnippetsViewLayoutProps } from './Snippets.view';
import { useRequiredQueries } from 'hooks/useRequiredQueries';
import { AppContext } from '../../../providers/AppProvider';

export type SnippetsApolloProps = {
  //
  onAddToPostClick?: SnippetCardViewProps['onAddToPostClick'];
  context: SnippetsViewLayoutProps['context'];
};

gql`
  query GetSnippets($workspaceId: ID!) {
    workspace(workspaceId: $workspaceId) {
      id
      snippets {
        ...CoreSnippetFields
      }
    }
  }

  mutation DeleteSnippet($snippetId: ID!) {
    deleteSnippet(snippetId: $snippetId)
  }
`;

export const SnippetsApollo = (props: SnippetsApolloProps): JSX.Element => {
  const { onAddToPostClick, context } = props;
  const { cache } = useApolloClient();
  const { selectedWorkspaceId } = useContext(AppContext);
  const getSnippetsLazyQuery = useGetSnippetsLazyQuery({
    variables: {
      workspaceId: selectedWorkspaceId
    }
  });
  const [getWorkspaceWithSnippetsQuery, workspaceWithSnippetsData] = getSnippetsLazyQuery;
  useRequiredQueries([getSnippetsLazyQuery]);
  const [deleteSnippetMutation, deleteSnippetMutationData] = useDeleteSnippetMutation();

  const refetchSnippets = workspaceWithSnippetsData.refetch;
  const deleteSnippetMutationHandler = useCallback(
    (options?: DeleteSnippetMutationOptions) => {
      // a little bit of optimism
      const normalizedId = cache.identify({ id: options?.variables?.snippetId, __typename: 'Snippet' });
      cache.evict({ id: normalizedId });
      cache.gc();

      return deleteSnippetMutation(options).catch(error => {
        // a little bit of pessimism
        refetchSnippets?.();
        throw new ApolloError(error);
      });
    },
    [cache, deleteSnippetMutation, refetchSnippets]
  );

  const mappedWorkspacesWithSnippetsToSnippets = useMemo(() => {
    // doing this because source query is getting snippets via requesting workspace data
    // but low-level components are not interested in this data shape, they're interested in snippets array and should not stick to
    // workspace-snippet relationship for easy changes in future
    const { data, loading } = workspaceWithSnippetsData;

    return {
      onAddToPostClick,
      error: workspaceWithSnippetsData.error,
      context,
      snippets: data?.workspace?.snippets ?? [],
      deleteSnippetMutationData,
      getSnippetsQuery: getWorkspaceWithSnippetsQuery,
      deleteSnippetMutation: deleteSnippetMutationHandler,
      snippetsLoading: loading,
      workspaceId: selectedWorkspaceId
    };
  }, [
    workspaceWithSnippetsData,
    onAddToPostClick,
    context,
    deleteSnippetMutationData,
    getWorkspaceWithSnippetsQuery,
    deleteSnippetMutationHandler,
    selectedWorkspaceId
  ]);

  return <SnippetsController {...mappedWorkspacesWithSnippetsToSnippets} />;
};
